import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Stream, StreamState, STREAM_STATES } from 'types/stream';
import { Gateway } from 'types/gateway';

import { useInputStreams } from 'streams/hooks/useInputStreams';
import { useMqttStreamsState } from 'streams/hooks/useMqttStreamsState';
import { useStreams, UseStreamsParams } from 'streams/hooks/useStreams';
import { useTagsFilterParams } from 'tags/hooks/useTagsFilterParams';

import * as PageLayout from 'components/PageLayout';
import { GatewaySelect } from 'gateways/components/GatewaySelect/GatewaySelect';
import { QuerySearch } from 'components/QuerySearch/QuerySearch';
import { StateSelect } from 'components/StateSelect/StateSelect';
import { ViewMode, ViewModeSwitch } from 'components/ViewModeSwitch/ViewModeSwitch';

import { InputStreamsTable } from './Table';

export function InputStreamsOverview() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const defaultStateFilter = state?.filters?.status;
  const [viewMode, setViewMode] = React.useState<ViewMode>('list');

  const tagFilters = useTagsFilterParams();
  const [queryFilters, setQueryFilters] = React.useState<
    UseStreamsParams & { page?: number }
  >({
    page: 1,
    status: defaultStateFilter ? [defaultStateFilter] : undefined,
  });
  const queryResult = useInputStreams(
    {
      ...tagFilters,
      ...queryFilters,
    },
    { keepPreviousData: true }
  );

  useMqttStreamsState(
    queryResult.data?.data ? queryResult.data.data.map(({ id }) => id) : []
  );

  const [searchFilters, setSearchFilters] = React.useState<
    UseStreamsParams & { page?: number }
  >({ page: 1 });
  const searchQueryResult = useStreams({
    params: {
      ...tagFilters,
      ...searchFilters,
      limit: 20,
      stream_types: ['rtsp', 'webrtc'],
      sources: ['uri_stream'],
    },
    enabled: Object.keys(searchFilters).length > 0,
  });

  const handleSearchInput = React.useCallback((value?: string) => {
    if (!value) {
      setSearchFilters({});
      setQueryFilters({ page: 1 });
      return;
    }

    setSearchFilters({
      stream_names: [value],
    });
  }, []);

  function handleApplyFilters() {
    setQueryFilters({ ...searchFilters, page: 1 });
  }

  function handleSuggestionSelect({ id }: Stream) {
    navigate(id);
  }

  const handleGatewayFilterChange = React.useCallback(
    (gateway: Gateway | null) => {
      setQueryFilters((previousFilters) => ({
        ...previousFilters,
        gateway_ids: gateway ? [gateway.id] : undefined,
        page: 1,
      }));
      setSearchFilters((previousFilters) => ({
        ...previousFilters,
        gateway_ids: gateway ? [gateway.id] : undefined,
      }));
    },
    []
  );

  function handleStateFilterChange(state: string | null) {
    setQueryFilters((previousFilters) => ({
      ...previousFilters,
      status: state ? [state as StreamState] : undefined,
      page: 1,
    }));

    setSearchFilters((previousFilters) => ({
      ...previousFilters,
      status: state ? [state as StreamState] : undefined,
    }));
  }

  const hasFilters =
    Object.keys(queryFilters).length > 0 ||
    Object.values(tagFilters).some(Boolean);

  return (
    <PageLayout.Root>
      <QuerySearch
        entityName="stream"
        searchLabel="Search input streams..."
        searchResultField="name"
        searchQueryResult={searchQueryResult}
        onApplyFilters={handleApplyFilters}
        onSuggestionSelect={handleSuggestionSelect}
        onValueChange={handleSearchInput}
        totalElements={queryResult.data?.total_elements}
        filters={
          <>
            <StateSelect
              onChange={handleStateFilterChange}
              options={STREAM_STATES}
              defaultValue={defaultStateFilter}
              size="small"
            />
            <GatewaySelect
              onChange={handleGatewayFilterChange}
              size="small"
              isClearable
            />
          </>
        }
        actions={<ViewModeSwitch value={viewMode} onChange={setViewMode} />}
      />

      <InputStreamsTable
        queryResult={queryResult}
        pageSize={50}
        page={queryFilters.page}
        onPageChange={(page) =>
          setQueryFilters((previous) => ({ ...previous, page }))
        }
        hasFilters={hasFilters}
        viewMode={viewMode}
      />
    </PageLayout.Root>
  );
}
